import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  Printer,
  Receipt,
  RefreshCw,
  Save,
  Trash2,
} from 'lucide-react';
import MainService from '../../services/MainService';
import { getApiBaseUrl } from '../../services/appSettings';
import { LedgerAccountAutocomplete } from './LedgerAccountAutocomplete';
import { VoucherLinesGrid } from './VoucherLinesGrid';
import {
  AlertBanner,
  Field,
  SectionCard,
  VoucherNoBadge,
  inputClass,
} from './accountingUi';
import {
  buildVoucherSavePayload,
  calcVoucherTotal,
  emptyVoucherDetail,
  fetchNextVoucherNumber,
  formatMoney,
  normalizeVoucherFromApi,
  padVoucherDetails,
  printAccountingVoucher,
  validateVoucherMaster,
} from '../../utils/accountingHelpers';
import { formatLocalDate } from '../../utils/dateLocal';
import { getVoucherConfig } from '../../utils/accountingVouchers';

function buildEmptyMaster(config) {
  const details = Array.from({ length: 5 }, () => emptyVoucherDetail());
  return {
    Id: 0,
    VoucherNumber: '',
    Remarks: '',
    AccountId: '',
    Account: null,
    BankAccountId: '',
    BankAccount: null,
    ChequeNo: '',
    chequeDateInput: '',
    maturityDateInput: formatLocalDate(),
    dateInput: formatLocalDate(),
    Total: 0,
    [config.detailKey]: details,
  };
}

const AccountingVoucherForm = () => {
  const { slug, voucherId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = voucherId === 'new';
  const config = useMemo(() => getVoucherConfig(slug), [slug]);
  const [master, setMaster] = useState(() => (config ? buildEmptyMaster(config) : null));
  const [loading, setLoading] = useState(false);
  const [loadingVoucherNo, setLoadingVoucherNo] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [printOnSave, setPrintOnSave] = useState(false);
  const [printing, setPrinting] = useState(false);

  const details = master ? master[config?.detailKey] || [] : [];
  const lineCount = useMemo(
    () => details.filter((row) => Number(row.AccountId) > 0 && Number(row.Amount) > 0).length,
    [details]
  );
  const total = useMemo(() => calcVoucherTotal(details), [details]);

  const loadVoucherNumber = useCallback(async () => {
    if (!config || !isNew) return;
    try {
      setLoadingVoucherNo(true);
      setError('');
      const voucherNo = await fetchNextVoucherNumber(config, MainService);
      if (!voucherNo) {
        setError('Could not load the next voucher number. Check Document Number settings.');
        return;
      }
      setMaster((prev) => ({ ...prev, VoucherNumber: voucherNo }));
    } catch (err) {
      console.error('Load voucher number error:', err);
      setError('Failed to load voucher number');
    } finally {
      setLoadingVoucherNo(false);
    }
  }, [config, isNew]);

  const loadDefaults = useCallback(async () => {
    if (!config || !isNew) return;
    try {
      setError('');
      const profileRes = await MainService.GetCompanyProfile();
      const profile = profileRes || {};
      const account = profile[config.defaultProfileKeys.account] || null;
      const accountId = profile[config.defaultProfileKeys.accountId] || '';
      const bankAccount = config.bankProfileKeys ? profile[config.bankProfileKeys.account] || null : null;
      const bankAccountId = config.bankProfileKeys ? profile[config.bankProfileKeys.accountId] || '' : '';
      setMaster((prev) => ({
        ...prev,
        Account: account,
        AccountId: accountId,
        BankAccount: bankAccount,
        BankAccountId: bankAccountId,
      }));
      await loadVoucherNumber();
    } catch (err) {
      console.error('Load voucher defaults error:', err);
      setError('Failed to load voucher defaults');
    }
  }, [config, isNew, loadVoucherNumber]);

  const loadVoucher = useCallback(async () => {
    if (!config || isNew || !voucherId) return;
    try {
      setLoading(true);
      setError('');
      const response = await MainService[config.apis.get](voucherId);
      const normalized = normalizeVoucherFromApi(response, config.detailKey);
      if (!normalized) {
        setError('Voucher not found');
        return;
      }
      const padded = padVoucherDetails(normalized[config.detailKey], config.detailKey);
      setMaster({ ...normalized, ...padded });
    } catch (err) {
      console.error('Load voucher error:', err);
      setError('Failed to load voucher');
    } finally {
      setLoading(false);
    }
  }, [config, voucherId, isNew]);

  useEffect(() => {
    if (!config) return;
    if (isNew) {
      setMaster(buildEmptyMaster(config));
      loadDefaults();
    } else {
      loadVoucher();
    }
  }, [config, isNew, loadDefaults, loadVoucher]);

  useEffect(() => {
    if (!error) return undefined;
    const t = setTimeout(() => setError(''), 6000);
    return () => clearTimeout(t);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const t = setTimeout(() => setSuccess(''), 3000);
    return () => clearTimeout(t);
  }, [success]);

  const updateDetail = (index, patch) => {
    if (!config) return;
    setMaster((prev) => {
      const list = [...(prev[config.detailKey] || [])];
      list[index] = { ...list[index], ...patch };
      return { ...prev, [config.detailKey]: list, Total: calcVoucherTotal(list) };
    });
  };

  const addDetailRow = () => {
    if (!config) return;
    setMaster((prev) => ({
      ...prev,
      [config.detailKey]: [...(prev[config.detailKey] || []), emptyVoucherDetail()],
    }));
  };

  const removeDetailRow = (index) => {
    if (!config) return;
    setMaster((prev) => {
      const list = [...(prev[config.detailKey] || [])];
      if (list.length <= 1) return prev;
      list.splice(index, 1);
      return { ...prev, [config.detailKey]: list, Total: calcVoucherTotal(list) };
    });
  };

  const handleDetailFocus = (index) => {
    if (!config) return;
    const list = master[config.detailKey] || [];
    if (index === list.length - 1) addDetailRow();
  };

  const validate = () => {
    if (isNew && !master.VoucherNumber?.trim()) {
      setError('Voucher number is required. Click Refresh number.');
      return false;
    }
    const message = validateVoucherMaster({
      accountId: master.AccountId,
      details,
    });
    if (message) {
      setError(message);
      return false;
    }
    if (config.hasBankAccount && !Number(master.BankAccountId)) {
      setError('Select bank account');
      return false;
    }
    if (config.hasChequeHeader) {
      if (!master.ChequeNo?.trim()) {
        setError('Enter cheque number');
        return false;
      }
      if (!master.chequeDateInput) {
        setError('Enter cheque date');
        return false;
      }
      if (!master.maturityDateInput) {
        setError('Enter maturity date');
        return false;
      }
    }
    return true;
  };

  const handlePrint = useCallback(async (id) => {
    const voucherIdToPrint = id || master?.Id;
    if (!config?.printType || !voucherIdToPrint) return false;
    try {
      setPrinting(true);
      setError('');
      const ok = await printAccountingVoucher(
        voucherIdToPrint,
        config.printType,
        MainService,
        getApiBaseUrl
      );
      if (!ok) setError('Failed to print voucher');
      return ok;
    } catch (err) {
      console.error('Print voucher error:', err);
      setError('Failed to print voucher');
      return false;
    } finally {
      setPrinting(false);
    }
  }, [config, master?.Id]);

  const save = async () => {
    if (!config || !validate()) return;
    try {
      setLoading(true);
      const payload = buildVoucherSavePayload(
        { ...master, VoucherNumber: master.VoucherNumber, Total: total },
        config
      );
      const response = await MainService[config.apis.save](payload);
      const ok = response?.Message === 'Success' || response?.message === 'Success';
      if (ok) {
        setSuccess('Voucher saved successfully');
        setConfirmOpen(false);
        const savedId = response.Id || master.Id;
        if (printOnSave && savedId) {
          await handlePrint(savedId);
        }
        if (isNew) {
          window.dispatchEvent(
            new CustomEvent('ainsoft:accounting-voucher-list-refresh', { detail: { slug } })
          );
          window.dispatchEvent(
            new CustomEvent('ainsoft:close-tab', {
              detail: {
                path: location.pathname,
                nextPath: `/home/accounting/vouchers/${slug}`,
              },
            })
          );
        } else {
          await loadVoucher();
        }
      } else {
        setError(response?.Message || response?.message || 'Failed to save voucher');
      }
    } catch (err) {
      setError(err?.message || 'Failed to save voucher');
    } finally {
      setLoading(false);
    }
  };

  const removeVoucher = async () => {
    if (!config || isNew) return;
    try {
      setLoading(true);
      await MainService[config.apis.delete](voucherId);
      setSuccess('Voucher deleted');
      window.dispatchEvent(
        new CustomEvent('ainsoft:accounting-voucher-list-refresh', { detail: { slug } })
      );
      window.dispatchEvent(
        new CustomEvent('ainsoft:close-tab', {
          detail: {
            path: location.pathname,
            nextPath: `/home/accounting/vouchers/${slug}`,
          },
        })
      );
    } catch {
      setError('Failed to delete voucher');
    } finally {
      setLoading(false);
      setDeleteOpen(false);
    }
  };

  if (!config || !master) {
    return (
      <div className="p-6">
        <p className="text-sm text-red-600">Unknown voucher type.</p>
      </div>
    );
  }

  return (
    <div className="voucher-form-page mx-auto flex min-h-[calc(100dvh-10rem)] max-w-7xl flex-col">
      <div className="flex-1 space-y-5 p-4 md:p-6">
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-gradient-to-r from-slate-900 via-slate-800 to-primary/90 text-white shadow-lg">
        <div className="flex flex-wrap items-start justify-between gap-4 p-5 md:p-6">
          <div className="flex min-w-0 items-start gap-4">
            <button
              type="button"
              onClick={() => navigate(`/home/accounting/vouchers/${slug}`)}
              className="mt-1 inline-flex rounded-lg border border-white/20 bg-white/10 p-2 text-white hover:bg-white/20"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div>
              <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium">
                <Receipt className="h-3.5 w-3.5" />
                {isNew ? 'New voucher' : 'Edit voucher'}
              </div>
              <h1 className="text-2xl font-bold tracking-tight">{config.title}</h1>
              <p className="mt-1 text-sm text-white/80">{config.shortTitle} voucher entry</p>
            </div>
          </div>
          <div className="rounded-2xl bg-white/10 p-4 backdrop-blur-sm">
            {isNew ? (
              <VoucherNoBadge
                value={master.VoucherNumber}
                loading={loadingVoucherNo}
                onRefresh={loadVoucherNumber}
                onDark
              />
            ) : (
              <div className="flex items-center gap-3">
                <div className="rounded-xl bg-white px-4 py-2.5 text-lg font-bold text-slate-900">
                  {master.VoucherNumber || '—'}
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-white/70">Voucher number</p>
                  <p className="text-sm text-white/90">Saved voucher</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <AlertBanner type="error" message={error} />
      <AlertBanner type="success" message={success} />

      {loading && !isNew ? (
        <div className="flex items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-white py-16 text-gray-500">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading voucher…
        </div>
      ) : (
        <>
          <SectionCard title="Voucher header" subtitle="Date, account, and reference details">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              <Field label="Voucher date">
                <input
                  type="date"
                  className={inputClass}
                  value={master.dateInput}
                  onChange={(e) => setMaster((p) => ({ ...p, dateInput: e.target.value }))}
                />
              </Field>
              <Field label={config.masterAccountLabel}>
                <LedgerAccountAutocomplete
                  value={master.Account}
                  source={config.masterAccountSource}
                  onSelect={(row) => setMaster((p) => ({ ...p, Account: row, AccountId: row.Id }))}
                />
              </Field>
              {config.hasBankAccount ? (
                <Field label={config.bankAccountLabel || 'Bank account'}>
                  <LedgerAccountAutocomplete
                    value={master.BankAccount}
                    source="bank"
                    onSelect={(row) => setMaster((p) => ({ ...p, BankAccount: row, BankAccountId: row.Id }))}
                  />
                </Field>
              ) : null}
              {config.hasChequeHeader ? (
                <>
                  <Field label="Cheque no">
                    <input
                      className={inputClass}
                      value={master.ChequeNo || ''}
                      onChange={(e) => setMaster((p) => ({ ...p, ChequeNo: e.target.value }))}
                    />
                  </Field>
                  <Field label="Cheque date">
                    <input
                      type="date"
                      className={inputClass}
                      value={master.chequeDateInput || ''}
                      onChange={(e) => setMaster((p) => ({ ...p, chequeDateInput: e.target.value }))}
                    />
                  </Field>
                  <Field label="Maturity date">
                    <input
                      type="date"
                      className={inputClass}
                      value={master.maturityDateInput || ''}
                      onChange={(e) => setMaster((p) => ({ ...p, maturityDateInput: e.target.value }))}
                    />
                  </Field>
                </>
              ) : null}
              <Field label="Remarks" className="md:col-span-2 xl:col-span-3">
                <textarea
                  className={inputClass}
                  rows={2}
                  placeholder="Optional notes for this voucher"
                  value={master.Remarks || ''}
                  onChange={(e) => setMaster((p) => ({ ...p, Remarks: e.target.value }))}
                />
              </Field>
            </div>
          </SectionCard>

          <SectionCard
            title="Voucher lines"
            subtitle="Enter account code, name, amount and narration per line"
            bodyClassName="p-4"
            className="mb-2"
          >
            <VoucherLinesGrid
              rows={details}
              config={config}
              total={total}
              onUpdateRow={updateDetail}
              onAddRow={addDetailRow}
              onRemoveRow={removeDetailRow}
              onRowFocus={handleDetailFocus}
            />
          </SectionCard>
        </>
      )}
      </div>

      <div className="sticky bottom-0 z-40 mt-4 shrink-0 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4 px-4 py-3 md:px-6">
          <div className="flex flex-wrap items-center gap-6">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Total amount</p>
              <p className="text-2xl font-bold text-gray-900">{formatMoney(total)}</p>
            </div>
            <div className="hidden h-10 w-px bg-gray-200 sm:block" />
            <div className="hidden sm:block">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Valid lines</p>
              <p className="text-lg font-semibold text-gray-800">{lineCount}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {!isNew ? (
              <button
                type="button"
                onClick={() => handlePrint()}
                disabled={loading || printing}
                className="inline-flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                <Printer className={`h-4 w-4 ${printing ? 'animate-pulse' : ''}`} />
                Print
              </button>
            ) : null}
            {!isNew ? (
              <button
                type="button"
                onClick={() => setDeleteOpen(true)}
                className="inline-flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-medium text-red-700 hover:bg-red-100"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </button>
            ) : null}
            {isNew ? (
              <button
                type="button"
                onClick={loadVoucherNumber}
                disabled={loadingVoucherNo}
                className="inline-flex items-center gap-2 rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <RefreshCw className={`h-4 w-4 ${loadingVoucherNo ? 'animate-spin' : ''}`} />
                Refresh no.
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => (validate() ? setConfirmOpen(true) : null)}
              disabled={loading || printing}
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-primary/20 hover:bg-primary/90 disabled:opacity-60"
            >
              <Save className="h-4 w-4" />
              Save voucher
            </button>
          </div>
        </div>
      </div>

      {confirmOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="text-lg font-semibold text-gray-900">Confirm save</h3>
            <p className="mt-2 text-sm text-gray-600">
              Save voucher <strong>{master.VoucherNumber}</strong> for total amount <strong>{formatMoney(total)}</strong>?
            </p>
            <label className="mt-4 flex cursor-pointer items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={printOnSave}
                onChange={(e) => setPrintOnSave(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
              />
              Print after save
            </label>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setConfirmOpen(false)} className="rounded-xl border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button type="button" onClick={save} disabled={loading || printing} className="rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90">
                Confirm save
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {deleteOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <h3 className="text-lg font-semibold text-gray-900">Delete voucher?</h3>
            <p className="mt-2 text-sm text-gray-600">This voucher will be marked inactive and removed from lists.</p>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setDeleteOpen(false)} className="rounded-xl border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button type="button" onClick={removeVoucher} className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700">
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AccountingVoucherForm;
